import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { EnvironmentInfo } from '@kemu-io/hs-types';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import useHubDefaultService, { DefaultServices, findService } from '@hooks/useHubDefaultService';
import { InstallServiceConfig } from '@src/types/hubService';
import connectionManager from '@src/app/kemuHub/connectionManager';
import useGlobalNotification from '@hooks/useGlobalNotification';
import useTranslation from '@hooks/useTranslation.ts';

/**
 * @returns a function that can be used to install Hub Services from the marketplace.
 */
const useInstallMarketplaceItem = () => {
  const [findDefaultHubService] = useHubDefaultService();
  const hubConnector = connectionManager.getConnector();
  const [installing, setInstalling] = useState<{name: string, version: string} | null>(null);
  const { showNotification } = useGlobalNotification();
  const t = useTranslation('CommonEvents');

  const installHubService = useCallback(async (config: {
    marketplaceItemId: string,
    name: string,
    version: string,
    title?: string
  }) => {
    const { marketplaceItemId, title } = config;
    try {
      let serviceInstaller = findDefaultHubService(DefaultServices.ServiceInstaller.name);

      // serviceInstaller is null: not in context and not in fresh cache
      // (e.g. user opened marketplace right after generating the default API key and the hub just restarted;
      // hub has no service installer; or not connected).
      if (!serviceInstaller) {
        console.warn('[useInstallMarketplaceItem] Service installer not in context, refreshing services cache and retrying', {
          marketplaceItemId,
          name: config.name,
          version: config.version,
        });
        await connectionManager.refreshServicesCache();
        const fresh = hubConnector.getCachedServices();
        
        // Use findService on the fresh cache: findDefaultHubService reads from React context,
        // which hasn't updated yet in this same handler (context updates on the next render).
        serviceInstaller = findService(fresh.available, DefaultServices.ServiceInstaller.name);
      }

      if (!serviceInstaller) {
        throw new Error(t('ServiceInstallerNotFound'));
      }

      const request: InstallServiceConfig = {
        replaceCurrentVersion: false,
        keepPreviousVersion: false,
        source: {
          marketplaceItem: {
            id: marketplaceItemId,
            name: config.name,
            version: config.version,
          },
        },
      };

      setInstalling({ name: config.name, version: config.version });
      const response = await hubConnector.callProcessorHandler(
        serviceInstaller.sessionId,
        {} as EnvironmentInfo,
        DefaultServices.ServiceInstaller.actions.installService,
        request,
        {
          // Wait for up to 15 minutes for the service installation to conclude
          timeout: 15 * 60 * 1000,
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      if (response.success) {
        const hasMissingSecrets = response.launchedWithErrors?.errorCode === 'MISSING_REQUIRED_SECRETS';
        const standardMessage = t('ServiceInstalled.Message', 'Message', { service: title ?? marketplaceItemId }).toString();
        const message = hasMissingSecrets
          ? t('ServiceInstalled.MissingSecrets', 'MissingSecrets', { service: title ?? marketplaceItemId }).toString()
          : standardMessage;

        showNotification({
          type: 'success',
          title: t('ServiceInstalled.Title').toString(),
          message,
          duration: 5,
        });

        await connectionManager.refreshServicesCache();
      }
    } catch (e) {
      console.error('Service install error:', e);
      showNotification({
        type: 'error',
        title: t('ServiceInstalledError.Title').toString(),
        message: e.message,
        duration: 5,
      });
    } finally {
      setInstalling(null);
    }
  }, [findDefaultHubService, t, hubConnector, showNotification]);

  const result = useMemo(() => ({
    /** Installs a HubService by id from the marketplace */
    installHubService,
    /** whether a service is currently being installed */
    installingService: installing
  }), [installing, installHubService]);

  return result;
};

export default useInstallMarketplaceItem;
